import type {
  CoachingClip,
  CoachingNote,
  DevelopmentProfile,
  DevelopmentRecentResponse,
} from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import {
  FeedbackThumb,
  FeedbackTonePill,
  ObservationThumb,
  noteTitle,
} from "./components/FeedbackCardBits";
import { BoardChip, boardHasContent } from "./components/BoardChip";
import { Button } from "@/components/ui/Button";
import { shortDateLabel } from "./feedbackHelpers";
import { PLAYER_RESPONSE_SIGNAL_CONFIG } from "./responseSignals";

/**
 * Player Development profile — the SHARED painter (M7).
 *
 * One component, two surfaces: the coach modal (opened from
 * Roster, `viewer={false}` — renders the coach-only Themes
 * section + linked accounts) and the viewer Development sub-tab
 * (`viewer={true}` — those are omitted). Ports the legacy
 * `_renderPlayerDevelopmentProfile` (`js/coaching/development.js`).
 *
 * Section order (legacy parity): header · counts · goals · themes
 * (coach-only) · focus areas · recent positives · recent
 * corrections · recent clips. Empty-state
 * collapse: if the profile has zero notes/clips/goals,
 * the header shows + ONE friendly message, not nine empty blocks.
 *
 * Privacy: the viewer payload OMITS `themes` + `linked_accounts`
 * (backend-gated — M7 inventory scope-correction). The painter
 * renders Themes only when `profile.themes` is present, so a
 * viewer payload structurally can't show it. `coach_private_note`
 * is never templated here regardless of surface.
 *
 * The painter is presentational — interaction (opening a note /
 * clip in the M5 modal) is delegated to the host via
 * optional `onOpen*` callbacks. The coach modal passes none (its
 * rows are read-only); the viewer tab wires them.
 */

export type PlayerDevelopmentProfileProps = {
  profile: DevelopmentProfile;
  /** `true` ⇒ viewer surface (omits Themes, viewer-tuned labels). */
  viewer: boolean;
  /** Viewer surface only — open a recent note in the M5 modal. */
  onOpenNote?: (note: CoachingNote) => void;
  /** Viewer surface only — open a recent clip in the M5 modal. */
  onOpenClip?: (clip: CoachingClip) => void;
  /** Optional slot under the Goals heading — Slice 6 mounts the
   * goal card list here. Until then the section shows its
   * empty/heading shell only. */
  goalsSlot?: React.ReactNode;
};

export function PlayerDevelopmentProfile({
  profile,
  viewer,
  onOpenNote,
  onOpenClip,
  goalsSlot,
}: PlayerDevelopmentProfileProps) {
  const c = profile.counts;
  const total = c.notes + c.clips + c.playlists + c.goals;

  return (
    <div
      className="flex flex-col gap-5"
      data-testid="player-development-profile"
      data-viewer-scoped={profile.viewer_scoped}
    >
      <DevHeader profile={profile} viewer={viewer} />

      {total === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="development-empty"
        >
          {viewer
            ? "No development feedback yet."
            : "No coaching activity for this player yet."}
        </p>
      ) : (
        <>
          <DevCounts profile={profile} viewer={viewer} />

          {/* Goals — Slice 6 fills `goalsSlot` with the card list. */}
          <Section
            title={viewer ? "Current goals" : "Active goals"}
            testid="development-goals"
          >
            {goalsSlot ?? (
              <p className="text-ink-muted text-sm">
                {profile.active_goals.length === 0
                  ? "No active goals yet."
                  : `${profile.active_goals.length} active goal${
                      profile.active_goals.length === 1 ? "" : "s"
                    }.`}
              </p>
            )}
          </Section>

          {/* Themes — COACH-ONLY. The viewer payload omits `themes`
           * entirely (backend-gated), so this never renders for a
           * viewer even without the `!viewer` guard — the guard is
           * belt-and-suspenders. */}
          {!viewer && profile.themes ? (
            <DevThemes profile={profile} />
          ) : null}

          {/* Recent responses — COACH-ONLY (PR 8). Decoded "How'd
           * it land?" reactions from each linked account. The
           * viewer payload omits `recent_responses` (defense in
           * depth — never leak other linked accounts' reflections
           * back to the player). */}
          {!viewer && profile.recent_responses ? (
            <DevRecentResponses
              responses={profile.recent_responses}
              onOpenSourceNote={onOpenNote}
              onOpenSourceClip={onOpenClip}
            />
          ) : null}

          <DevFocusAreas profile={profile} viewer={viewer} />

          <DevNoteList
            title="Recent positives"
            emptyLabel="No positives yet."
            notes={profile.recent_positives}
            viewer={viewer}
            onOpenNote={onOpenNote}
            testid="development-recent-positives"
          />
          <DevNoteList
            title={viewer ? "Recent things to work on" : "Recent corrections"}
            emptyLabel={
              viewer ? "Nothing to work on right now." : "No corrections yet."
            }
            notes={profile.recent_corrections}
            viewer={viewer}
            emphasizeNext
            onOpenNote={onOpenNote}
            testid="development-recent-corrections"
          />
          <DevClipList
            clips={profile.recent_clips}
            onOpenClip={onOpenClip}
          />
        </>
      )}
    </div>
  );
}

// --- Header ----------------------------------------------------------

function DevHeader({
  profile,
  viewer,
}: {
  profile: DevelopmentProfile;
  viewer: boolean;
}) {
  const p = profile.player;
  return (
    <header data-testid="development-header">
      <div className="flex items-center gap-2">
        {p.jersey_number ? (
          <span className="text-ink-muted font-mono text-sm">
            #{p.jersey_number}
          </span>
        ) : null}
        <h3 className="text-xl font-display text-ink">
          {p.display_name || "Player"}
        </h3>
        <span
          className={[
            "rounded px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider",
            p.active
              ? "bg-accent/15 text-accent"
              : "bg-surface text-ink-muted",
          ].join(" ")}
        >
          {p.active ? "Active" : "Inactive"}
        </span>
      </div>

      {/* Coach-only: the player's free-text notes field. */}
      {!viewer && p.notes_field ? (
        <p className="text-ink-muted text-sm mt-1">{p.notes_field}</p>
      ) : null}

      {/* Coach-only: linked accounts. The viewer payload omits
       * `linked_accounts` (backend-gated). */}
      {!viewer &&
      profile.linked_accounts &&
      profile.linked_accounts.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 mt-2"
          data-testid="development-linked-accounts"
        >
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Linked accounts ({profile.linked_accounts.length})
          </span>
          {profile.linked_accounts.map((l) => (
            <span
              key={l.user_id}
              className="rounded px-2 py-0.5 text-xs bg-surface text-ink"
            >
              @{l.username} · {l.relationship}
            </span>
          ))}
        </div>
      ) : null}
    </header>
  );
}

// --- Counts ----------------------------------------------------------

function DevCounts({
  profile,
  viewer,
}: {
  profile: DevelopmentProfile;
  viewer: boolean;
}) {
  const c = profile.counts;
  const r = profile.review_status;
  const tiles: Array<{ label: string; value: string; hint?: string }> = [
    { label: viewer ? "Notes shared" : "Notes", value: String(c.notes) },
    { label: viewer ? "Clips shared" : "Clips", value: String(c.clips) },
    { label: viewer ? "Active goals" : "Goals", value: String(c.goals) },
    {
      label: "Notes reviewed",
      value: `${r.notes.reviewed_count} / ${r.notes.assigned_count}`,
    },
    { label: "Reflections", value: String(r.reflection_count) },
  ];
  // Coach-only: surface that clip-review isn't tracked yet (vs a
  // misleading "0 / N").
  if (!viewer && r.clips.review_supported === false) {
    tiles.push({
      label: "Clips reviewed",
      value: "—",
      hint: "Clip review not tracked yet",
    });
  }
  return (
    <Section title="Summary" testid="development-counts">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {tiles.map((t) => (
          <div
            key={t.label}
            title={t.hint}
            className="rounded border border-rule bg-surface px-3 py-2 shadow-sm"
          >
            <div className="text-ink-muted text-[0.65rem] font-display uppercase tracking-wider">
              {t.label}
            </div>
            <strong className="mt-0.5 block text-xl font-display text-ink tabular-nums">
              {t.value}
            </strong>
          </div>
        ))}
      </div>
    </Section>
  );
}

// --- Themes (coach-only) ---------------------------------------------

const THEME_TYPE_LABELS: Record<string, string> = {
  positive: "Positives",
  correction: "Corrections",
  question: "Questions",
  team_concept: "Team concepts",
  individual_goal: "Individual goals",
};
const THEME_TYPE_ORDER = [
  "positive",
  "correction",
  "question",
  "team_concept",
  "individual_goal",
];

function DevThemes({ profile }: { profile: DevelopmentProfile }) {
  const t = profile.themes!;
  const typeChips = THEME_TYPE_ORDER.filter(
    (k) => (t.by_note_type[k] ?? 0) > 0,
  );
  const ratio =
    t.positive_to_correction_ratio === null ||
    t.positive_to_correction_ratio === undefined
      ? "—"
      : t.positive_to_correction_ratio.toFixed(2);

  return (
    <Section title="Themes" testid="development-themes">
      {typeChips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {typeChips.map((k) => (
            <span
              key={k}
              data-feedback-tone={k}
              className="rounded px-2 py-0.5 text-xs bg-surface text-ink"
            >
              {THEME_TYPE_LABELS[k]} <strong>{t.by_note_type[k]}</strong>
            </span>
          ))}
        </div>
      ) : null}
      <p className="text-ink-muted text-xs mt-2">
        Positive : Correction ratio <strong className="text-ink">{ratio}</strong>
      </p>
      {t.top_categories.length > 0 ? (
        <div className="mt-2">
          <span className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
            Top categories
          </span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {t.top_categories.map((cat) => (
              <span
                key={cat.value}
                className="rounded px-2 py-0.5 text-xs bg-surface text-ink"
              >
                {cat.value || "—"} <strong>{cat.count}</strong>
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {t.top_tags.length > 0 ? (
        <div className="mt-2">
          <span className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
            Top tags
          </span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {t.top_tags.map((tag) => (
              <span
                key={tag.value}
                className="rounded px-2 py-0.5 text-xs bg-surface text-ink-muted"
              >
                #{tag.value} <strong>{tag.count}</strong>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </Section>
  );
}

// --- Focus areas -----------------------------------------------------

function DevFocusAreas({
  profile,
  viewer,
}: {
  profile: DevelopmentProfile;
  viewer: boolean;
}) {
  const focus = profile.current_focus_areas;
  const title = viewer
    ? "Focus areas from recent feedback"
    : "Suggested focus areas from recent notes";
  return (
    <Section title={title} testid="development-focus-areas">
      {focus.length === 0 ? (
        <p className="text-ink-muted text-sm">No focus areas yet.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {focus.map((f) => (
            <li
              key={f.note_id}
              className="rounded border border-rule bg-surface px-3 py-2 shadow-sm"
            >
              <span className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
                {f.category || f.note_type || ""}
              </span>
              <p className="text-ink text-sm mt-0.5">
                {f.what_to_do_next}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// --- Recent note list ------------------------------------------------

function DevNoteList({
  title,
  emptyLabel,
  notes,
  viewer,
  emphasizeNext,
  onOpenNote,
  testid,
}: {
  title: string;
  emptyLabel: string;
  notes: CoachingNote[];
  viewer: boolean;
  emphasizeNext?: boolean;
  onOpenNote?: (note: CoachingNote) => void;
  testid: string;
}) {
  return (
    <Section title={title} testid={testid}>
      {notes.length === 0 ? (
        <p className="text-ink-muted text-sm">{emptyLabel}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {notes.map((note) => (
            <DevNoteItem
              key={note.id}
              note={note}
              viewer={viewer}
              emphasizeNext={emphasizeNext}
              onOpenNote={onOpenNote}
            />
          ))}
        </ul>
      )}
    </Section>
  );
}

function DevNoteItem({
  note,
  viewer,
  emphasizeNext,
  onOpenNote,
}: {
  note: CoachingNote;
  viewer: boolean;
  emphasizeNext?: boolean;
  onOpenNote?: (note: CoachingNote) => void;
}) {
  const isObservation = (note.note_context ?? "video") === "observation";
  const title = noteTitle(note);
  const summary =
    note.player_summary?.trim() || note.body?.trim() || "";
  const next = note.what_to_do_next?.trim() || "";

  const metaParts: string[] = [];
  if (isObservation) {
    if (note.event_type) {
      const t = note.event_type;
      metaParts.push(`${t[0].toUpperCase()}${t.slice(1)} observation`);
    } else {
      metaParts.push("Observation");
    }
    if (note.event_date) metaParts.push(note.event_date);
  } else {
    if (note.match_id) metaParts.push(note.match_id);
    metaParts.push(formatPlayhead(note.timestamp_seconds));
  }

  // Viewer rows are clickable (open the M5 modal); coach rows are
  // read-only — the coach surface mounts its own modal context.
  const clickable = viewer && !!onOpenNote;
  const interactive = clickable
    ? {
        role: "button" as const,
        tabIndex: 0,
        onClick: () => onOpenNote!(note),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenNote!(note);
          }
        },
        "aria-label": `Open ${title || "note"}`,
      }
    : {};

  return (
    <li
      {...interactive}
      data-testid="development-note-item"
      data-note-context={isObservation ? "observation" : "video"}
      className={[
        "flex items-start gap-3 rounded border border-rule bg-surface px-3 py-2 shadow-sm",
        clickable
          ? "cursor-pointer transition-colors hover:bg-field focus:outline-none focus:ring-2 focus:ring-accent"
          : "",
      ].join(" ")}
    >
      {/* Same observation-with-board parity as the Notes tab —
       * the dev painter's recent-note items mirror the legacy
       * `_renderDevNoteItem` chip variants. */}
      {isObservation ? (
        boardHasContent(note.tactical_board_json) ? (
          <BoardChip raw={note.tactical_board_json} />
        ) : (
          <ObservationThumb />
        )
      ) : (
        <FeedbackThumb kind="note" id={note.id} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <FeedbackTonePill noteType={note.note_type} />
          {title ? (
            <strong className="text-ink text-sm truncate">
              {title}
            </strong>
          ) : null}
        </div>
        {metaParts.length > 0 ? (
          <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
            {metaParts.join(" · ")}
          </div>
        ) : null}
        {summary ? (
          <p className="text-ink text-sm mt-1">{summary}</p>
        ) : null}
        {next ? (
          <p
            className={[
              "text-sm mt-1",
              emphasizeNext ? "text-ink" : "text-ink-muted",
            ].join(" ")}
          >
            <span className="font-display text-xs uppercase tracking-wider text-ink-muted">
              {viewer ? "Try this next: " : "What to do next: "}
            </span>
            {next}
          </p>
        ) : null}
      </div>
    </li>
  );
}

// --- Recent clips ----------------------------------------------------

function DevClipList({
  clips,
  onOpenClip,
}: {
  clips: CoachingClip[];
  onOpenClip?: (clip: CoachingClip) => void;
}) {
  return (
    <Section title="Recent clips" testid="development-recent-clips">
      {clips.length === 0 ? (
        <p className="text-ink-muted text-sm">No clips yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {clips.map((clip) => {
            const title = clip.title?.trim() || "(untitled clip)";
            const meta = `${clip.match_id} · ${formatPlayhead(
              clip.start_seconds,
            )}–${formatPlayhead(clip.end_seconds)}`;
            return (
              <li
                key={clip.id}
                data-testid="development-clip-item"
                className="flex items-center gap-3 rounded border border-rule bg-surface px-3 py-2 shadow-sm"
              >
                <FeedbackThumb kind="clip" id={clip.id} />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-ink text-sm truncate">
                    {title}
                  </div>
                  <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
                    {meta}
                  </div>
                </div>
                {onOpenClip ? (
                  <Button
                    type="button"
                    onClick={() => onOpenClip(clip)}
                    data-testid="development-clip-watch"
                    size="sm"
                    className="shrink-0"
                  >
                    ▶ Watch
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

// --- Recent responses (PR 8 — coach-only) -----------------------------

/** Coach-side "How'd it land?" feed inside the Development modal.
 * Renders each decoded reflection as a chip + responder name +
 * body, with an "Open" link that routes to the source item via the
 * host's existing modal-open handlers. */
function DevRecentResponses({
  responses,
  onOpenSourceNote,
  onOpenSourceClip,
}: {
  responses: DevelopmentRecentResponse[];
  onOpenSourceNote?: (n: CoachingNote) => void;
  onOpenSourceClip?: (c: CoachingClip) => void;
}) {
  const visibleResponses = responses.filter(
    (response) => response.item_kind !== "playlist",
  );
  if (visibleResponses.length === 0) {
    return (
      <Section title="Recent responses" testid="development-recent-responses">
        <p className="text-ink-muted text-sm">
          No reactions yet. When a family account taps Got it / Need help /
          I tried this on a note or clip, it shows up here.
        </p>
      </Section>
    );
  }
  // Sort "need_help" rows to the very top — coach attention follows
  // the orange flag. Other rows keep their server-side DESC order.
  const sorted = [...visibleResponses].sort((a, b) => {
    const aNeedsHelp = a.signal === "need_help";
    const bNeedsHelp = b.signal === "need_help";
    if (aNeedsHelp && !bNeedsHelp) return -1;
    if (!aNeedsHelp && bNeedsHelp) return 1;
    return 0;
  });
  return (
    <Section title="Recent responses" testid="development-recent-responses">
      <ul className="flex flex-col gap-2">
        {sorted.map((r, idx) => (
          <DevRecentResponseRow
            key={`${r.item_kind}-${r.item_id}-${r.responder.user_id}-${idx}`}
            response={r}
            onOpenSourceNote={onOpenSourceNote}
            onOpenSourceClip={onOpenSourceClip}
          />
        ))}
      </ul>
    </Section>
  );
}

function DevRecentResponseRow({
  response,
  onOpenSourceNote,
  onOpenSourceClip,
}: {
  response: DevelopmentRecentResponse;
  onOpenSourceNote?: (n: CoachingNote) => void;
  onOpenSourceClip?: (c: CoachingClip) => void;
}) {
  const config = response.signal
    ? PLAYER_RESPONSE_SIGNAL_CONFIG[response.signal]
    : null;
  const dateLabel = response.reviewed_at
    ? shortDateLabel(response.reviewed_at)
    : "";
  // The painter doesn't have the full note/clip object — the dev
  // profile only carries the id + title. Construct a thin shim for
  // the open-callback if the host has wired one. (PR 8 keeps the
  // wiring minimal — Coach Review's "open note" flow needs more
  // context than this section has, so the "Open" link is best-effort
  // and falls back to a no-op when the host hasn't supplied a
  // handler.)
  const canOpen =
    (response.item_kind === "note" && !!onOpenSourceNote) ||
    (response.item_kind === "clip" && !!onOpenSourceClip);
  const responderName =
    response.responder.display_name?.trim() ||
    response.responder.username?.trim() ||
    "Family account";
  const relationship = response.responder.relationship?.trim();
  const itemTitle =
    response.item_title?.trim() ||
    `${
      response.item_kind === "note"
        ? "Note"
        : response.item_kind === "clip"
          ? "Clip"
          : "Feedback"
    } #${response.item_id}`;

  return (
    <li
      data-testid="development-recent-response"
      data-response-signal={response.signal ?? "none"}
      data-response-item-kind={response.item_kind}
      className="flex flex-col gap-1.5 rounded border border-rule bg-surface px-3 py-2 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        {config ? (
          <ResponseSignalChip signal={response.signal!} config={config} />
        ) : (
          <span
            className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted border border-rule rounded-full px-2 py-0.5"
            data-testid="development-recent-response-no-signal"
          >
            Reflection
          </span>
        )}
        <span className="text-sm font-display text-ink">{responderName}</span>
        {relationship ? (
          <span className="text-[0.65rem] font-mono uppercase tracking-wider text-ink-muted">
            {relationship}
          </span>
        ) : null}
        {dateLabel ? (
          <span className="text-[0.65rem] font-mono uppercase tracking-wider text-ink-muted ml-auto">
            {dateLabel}
          </span>
        ) : null}
      </div>
      <p className="text-sm text-ink-muted">
        <span className="font-display text-ink">on </span>
        {itemTitle}
        {response.body ? (
          <>
            <span className="font-display text-ink"> — </span>
            <span className="italic">"{response.body}"</span>
          </>
        ) : null}
      </p>
      {canOpen ? (
        <button
          type="button"
          data-testid="development-recent-response-open"
          onClick={() => {
            // The painter doesn't have the full source object, so
            // these callbacks accept the lightweight equivalents we
            // do have. The note + clip variants take partial shims;
            // the host is responsible for re-fetching if it needs
            // full data (Coach Review already does this).
            if (response.item_kind === "note") {
              onOpenSourceNote?.({ id: response.item_id } as unknown as CoachingNote);
            } else if (response.item_kind === "clip") {
              onOpenSourceClip?.({ id: response.item_id } as unknown as CoachingClip);
            }
          }}
          className="self-start text-[0.7rem] font-display uppercase tracking-wider text-accent hover:underline"
        >
          Open →
        </button>
      ) : null}
    </li>
  );
}

function ResponseSignalChip({
  signal,
  config,
}: {
  signal: "got_it" | "need_help" | "tried_this";
  config: { label: string; glyph: string };
}) {
  // Tone-mapped color treatment mirrors the player's chip palette so
  // the coach reads the same visual language the player sees.
  const cls =
    signal === "got_it"
      ? "bg-good/15 text-good"
      : signal === "need_help"
        ? "bg-flag/15 text-flag"
        : "bg-accent/15 text-accent-ink";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider ${cls}`}
      data-testid="development-recent-response-chip"
      data-signal={signal}
    >
      <span aria-hidden="true">{config.glyph}</span>
      <span>{config.label}</span>
    </span>
  );
}

// --- Section shell ---------------------------------------------------

function Section({
  title,
  testid,
  children,
}: {
  title: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <section data-testid={testid}>
      <h4 className="text-sm font-display uppercase tracking-wider text-ink-muted mb-2">
        {title}
      </h4>
      {children}
    </section>
  );
}
